import styled from "@emotion/styled";
import { User } from "@ecologi/shared";
import { Link } from "@ecologi/react-components";
import { NavSection } from "@ecologi/react-components/src/NavigationDrawer/navigation";
import { accountDetailsPageTitle } from "../../features/profile/account-details/account-details-page-title";
import { isProduction } from "../../lib/utils/is-production";
import { getPhoenixUrl } from "../../lib";
import { addIdsToNavSection } from "./add-ids-to-nav-section";

const BasketBadgeLink = styled(Link)`
  display: flex;
  width: 50px;
  height: 45px;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  :hover {
    background-color: ${({ theme }) =>
      theme.colors.opacity(theme.colors.snowdrop, 0.03)};
  }
`;

const CountBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.colors.jungleLight};
  font-family: ${({ theme }) => theme.typography.fontStack};
  font-weight: 700;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.forestDark};
`;

export const getNavSectionsForFundPlatform = ({
  productsInBasket,
  user,
}: {
  user: User;
  productsInBasket?: number;
}): NavSection[] => {
  return [
    [
      {
        label: "Your profile",
        path: user.getProfilePath(),
        route: user.isChildProfile
          ? "/[usernameOrId]/[childUsernameOrId]"
          : "/[usernameOrId]",
      },
      {
        label: "Dashboard",
        path: "/dashboard",
      },
      {
        label: "Project insights",
        path: "/project-insights",
      },
      {
        label: "Impact shop",
        path: "/impact-shop",
        rightSlot:
          productsInBasket > 0 ? (
            <BasketBadgeLink href="/impact-shop/basket">
              <CountBadge>{productsInBasket}</CountBadge>
            </BasketBadgeLink>
          ) : undefined,
      },
      {
        label: "Badges",
        path: `/${user.username}/badges`,
        route: "/[usernameOrId]/badges",
      },
      ...(user.isBusinessType
        ? [
            {
              label: "Business toolkit",
              path: "/business-toolkit",
            },
            {
              label: "Impact API",
              path: "/impact-api",
            },
          ]
        : []),
      {
        label: "Account",
        links: [
          {
            label: accountDetailsPageTitle(user),
            path: "/account/details",
          },
          {
            label: "Profile settings",
            path: "/account/settings",
          },
          {
            label: "Billing",
            path: "/account/billing",
          },
          {
            label: "Manage subscription",
            path: "/account/manage-subscription",
          },
          {
            label: "Certificates",
            path: "/account/certificates",
          },
        ],
      },
    ],
    ...(user.isBusinessType
      ? [
          [
            {
              label: "Ecologi Zero",
              path: isProduction()
                ? "https://zero.ecologi.com/api/landing-redirect"
                : "https://x.zero.ecologi.com/api/landing-redirect",
            },
          ],
        ]
      : []),
    [
      {
        label: "Help centre",
        path: "https://help.ecologi.com/",
      },
      ...(user.isBusinessType
        ? [
            {
              label: "Switch to new app",
              path: getPhoenixUrl(),
              tag: { text: "beta" },
            },
          ]
        : []),
    ],
    [
      {
        label: " Logout",
        path: "/api/logout",
      },
    ],
  ].map(addIdsToNavSection);
};
